import { http } from '@/api/http';
import { ApiResponseBody } from '@/api/types';

import {
  DateRangeParams,
  getCSScheduleApiUrl,
  getSScheduleApiUrl,
  getCCScheduleApiUrl,
  getOMSScheduleApiUrl,
  Region,
  SetCSShiftTypeRequestBody,
  ResetShiftTypeRequestBody,
  SetFixationTypeRequestBody,
  CSShift,
  CSResourceRulesSettings,
  CSRequirementsInputsSettings,
  CSShiftsResponseBody,
  MonthSchedule,
  PlanningProcess,
  CSShiftTypeScheduleValidationResult,
  SShiftsResponseBody,
  SShift,
  SetSShiftTypeRequestBody,
  SShiftTypeScheduleValidationResult,
  SResourceRulesSettings,
  SRequirementsInputsSettings,
  SRegion,
  CCShiftsResponseBody,
  SetCCShiftTypeRequestBody,
  CCShift,
  CCShiftTypeScheduleValidationResult,
  CCResourceRulesSettings,
  CCRequirementsInputsSettings,
  SetMyCCShiftTypeRequestBody,
  ResetMyShiftTypeRequestBody,
  CSResourceCSShiftTypeHrSystemConflict,
  SResourceSShiftTypeHrSystemConflict,
  SResolveHrSystemConflict,
  CSResolveHrSystemConflict,
  CCResourceCCShiftTypeHrSystemConflict,
  CCResolveHrSystemConflict,
  OMSShiftsResponseBody,
  SetOMSShiftTypeRequestBody,
  OMSShift,
  OMSResourceOMSShiftTypeHrSystemConflict,
  OMSResolveHrSystemConflict,
  OMSShiftTypeScheduleValidationResult,
  OMSResourceRulesSettings,
  OMSRequirementsInputsSettings,
  CSTimeGroupRequirementsInputsSettings,
  CSAtHubWeeklyRequirementsInputsSettings,
  CSPeriodResourceRulesSettings,
  CSDeductionShiftRulesSettings,
} from '@/api/apis/resources/schedule';

const getCSShifts = async (params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<CSShiftsResponseBody>>(getCSScheduleApiUrl.csShifts(), { params });
  return res.data.data;
};

const getCSRegions = async () => {
  const res = await http.get<ApiResponseBody<Region[]>>(getCSScheduleApiUrl.csRegions());
  return res.data.data;
};

const setCSShiftType = async (body: SetCSShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<CSShift>>(getCSScheduleApiUrl.csSetShiftType(), body);
  return res.data.data;
};

const resetCSShiftType = async (body: ResetShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getCSScheduleApiUrl.csResetShiftType(), body);
  return res.data.data;
};

const setCSFixationType = async (body: SetFixationTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getCSScheduleApiUrl.csSetFixationType(), body);
  return res.data.data;
};

const getCSSchedule = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<MonthSchedule>>(getCSScheduleApiUrl.csSchedule(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getCSPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getCSScheduleApiUrl.csPlanningProcesses(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getCSPlanningProcessById = async (processId: string) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getCSScheduleApiUrl.csPlanningProcessesById(processId));
  return res.data.data;
};

const createCSPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(getCSScheduleApiUrl.csPlanningProcesses(), null, {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const regenerateCSPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCSScheduleApiUrl.csPlanningProcessesRegenerateById(processId),
  );
  return res.data.data;
};

const cancelGenerationCSPlanningProcess = async (processId: string, generationJobId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCSScheduleApiUrl.csPlanningProcessesCancelGenerationById(processId),
    null,
    { params: { generationJobId } },
  );
  return res.data.data;
};

const cancelCSPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCSScheduleApiUrl.csPlanningProcessesCancelById(processId),
  );
  return res.data.data;
};

const publishCSPlanningProcess = async (processId: string, scheduleId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCSScheduleApiUrl.csPlanningProcessesPublishById(processId),
    null,
    { params: { scheduleId } },
  );
  return res.data.data;
};

const getCSPlanningShifts = async (processId: string, params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<CSShiftsResponseBody>>(
    getCSScheduleApiUrl.csPlanningProcessesShiftsById(processId),
    { params },
  );
  return res.data.data;
};

const getCSValidation = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<CSShiftTypeScheduleValidationResult>>(
    getCSScheduleApiUrl.csScheduleValidation(scheduleId),
  );
  return res.data.data;
};

const csScheduleRefreshHr = async (scheduleId: string) => {
  const res = await http.post<ApiResponseBody<null>>(getCSScheduleApiUrl.csScheduleRefreshHr(scheduleId));
  return res.data.data;
};

const getCSScheduleHrSystemConflicts = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<CSResourceCSShiftTypeHrSystemConflict[]>>(
    getCSScheduleApiUrl.csScheduleHrSystemConflicts(scheduleId),
  );
  return res.data.data;
};

const resolveCSScheduleHrSystemConflict = async (body: CSResolveHrSystemConflict) => {
  const res = await http.post<ApiResponseBody<CSShift>>(getCSScheduleApiUrl.csScheduleHrSystemConflictsResolve(), body);
  return res.data.data;
};

/** SETTINGS */
const getCSScheduleRules = async () => {
  const res = await http.get<ApiResponseBody<CSResourceRulesSettings>>(getCSScheduleApiUrl.csSettingsRules());
  return res.data.data;
};

const setCSScheduleRules = async (body: CSResourceRulesSettings) => {
  const res = await http.post<ApiResponseBody<CSResourceRulesSettings>>(getCSScheduleApiUrl.csSettingsRules(), body);
  return res.data.data;
};

const getCSScheduleInputs = async () => {
  const res = await http.get<ApiResponseBody<CSRequirementsInputsSettings>>(getCSScheduleApiUrl.csSettingsInputs());
  return res.data.data;
};

const setCSScheduleInputs = async (body: CSRequirementsInputsSettings) => {
  const res = await http.post<ApiResponseBody<CSRequirementsInputsSettings>>(
    getCSScheduleApiUrl.csSettingsInputs(),
    body,
  );
  return res.data.data;
};

/** SETTINGS - PERIOD FAMILY */
const getCSSchedulePeriodTimeGroupInputs = async () => {
  const res = await http.get<ApiResponseBody<CSTimeGroupRequirementsInputsSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodTimeGroupInputs(),
  );
  return res.data.data;
};

const setCSSchedulePeriodTimeGroupInputs = async (body: CSTimeGroupRequirementsInputsSettings) => {
  const res = await http.post<ApiResponseBody<CSTimeGroupRequirementsInputsSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodTimeGroupInputs(),
    body,
  );
  return res.data.data;
};

const getCSSchedulePeriodRules = async () => {
  const res = await http.get<ApiResponseBody<CSPeriodResourceRulesSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodRules(),
  );
  return res.data.data;
};

const setCSSchedulePeriodRules = async (body: CSPeriodResourceRulesSettings) => {
  const res = await http.post<ApiResponseBody<CSPeriodResourceRulesSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodRules(),
    body,
  );
  return res.data.data;
};

const getCSSchedulePeriodAtHubWeeklyInputs = async () => {
  const res = await http.get<ApiResponseBody<CSAtHubWeeklyRequirementsInputsSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodAtHubWeekly(),
  );
  return res.data.data;
};

const setCSSchedulePeriodAtHubWeeklyInputs = async (body: CSAtHubWeeklyRequirementsInputsSettings) => {
  const res = await http.post<ApiResponseBody<CSAtHubWeeklyRequirementsInputsSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodAtHubWeekly(),
    body,
  );
  return res.data.data;
};

const getCSSchedulePeriodDeductionShiftRules = async () => {
  const res = await http.get<ApiResponseBody<CSDeductionShiftRulesSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodDeductionShiftRules(),
  );
  return res.data.data;
};

const setCSSchedulePeriodDeductionShiftRules = async (body: CSDeductionShiftRulesSettings) => {
  const res = await http.post<ApiResponseBody<CSDeductionShiftRulesSettings>>(
    getCSScheduleApiUrl.csSettingsPeriodDeductionShiftRules(),
    body,
  );
  return res.data.data;
};

const csScheduleApiFetcher = {
  getCSSchedule,
  getCSShifts,
  getCSRegions,
  setCSShiftType,
  resetCSShiftType,
  setCSFixationType,
  getCSPlanningProcess,
  getCSPlanningProcessById,
  createCSPlanningProcess,
  cancelCSPlanningProcess,
  publishCSPlanningProcess,
  regenerateCSPlanningProcess,
  cancelGenerationCSPlanningProcess,
  getCSPlanningShifts,
  getCSValidation,
  getCSScheduleRules,
  setCSScheduleRules,
  getCSScheduleInputs,
  setCSScheduleInputs,
  getCSSchedulePeriodTimeGroupInputs,
  setCSSchedulePeriodTimeGroupInputs,
  getCSSchedulePeriodRules,
  setCSSchedulePeriodRules,
  getCSSchedulePeriodAtHubWeeklyInputs,
  setCSSchedulePeriodAtHubWeeklyInputs,
  getCSSchedulePeriodDeductionShiftRules,
  setCSSchedulePeriodDeductionShiftRules,
  csScheduleRefreshHr,
  getCSScheduleHrSystemConflicts,
  resolveCSScheduleHrSystemConflict,
};

const getSLiShifts = async (params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<SShiftsResponseBody>>(getSScheduleApiUrl.sLiShifts(), { params });
  return res.data.data;
};
const getSHtLuShifts = async (params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<SShiftsResponseBody>>(getSScheduleApiUrl.sHtLuShifts(), { params });
  return res.data.data;
};

const getSLiRegions = async () => {
  const res = await http.get<ApiResponseBody<SRegion[]>>(getSScheduleApiUrl.sLiRegions());
  return res.data.data;
};
const getSHtLuRegions = async () => {
  const res = await http.get<ApiResponseBody<SRegion[]>>(getSScheduleApiUrl.sHtLuRegions());
  return res.data.data;
};

const setSLiShiftType = async (body: SetSShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<SShift>>(getSScheduleApiUrl.sLiSetShiftType(), body);
  return res.data.data;
};

const setSHtLuShiftType = async (body: SetSShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<SShift>>(getSScheduleApiUrl.sHtLuSetShiftType(), body);
  return res.data.data;
};

const resetSLiShiftType = async (body: ResetShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getSScheduleApiUrl.sLiResetShiftType(), body);
  return res.data.data;
};

const resetSHtLuShiftType = async (body: ResetShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getSScheduleApiUrl.sHtLuResetShiftType(), body);
  return res.data.data;
};

const setSFixationType = async (body: SetFixationTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getSScheduleApiUrl.sSetFixationType(), body);
  return res.data.data;
};

const getSLiSchedule = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<MonthSchedule>>(getSScheduleApiUrl.sLiSchedule(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};
const getSHtLuSchedule = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<MonthSchedule>>(getSScheduleApiUrl.sHtLuSchedule(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getSLiPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getSScheduleApiUrl.sLiPlanningProcesses(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};
const getSHtLuPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getSScheduleApiUrl.sHtLuPlanningProcesses(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getSPlanningProcessById = async (processId: string) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getSScheduleApiUrl.sPlanningProcessesById(processId));
  return res.data.data;
};

const createSLiPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(getSScheduleApiUrl.sLiPlanningProcesses(), null, {
    params: { startDayOfMonth },
  });
  return res.data.data;
};
const createSHtLuPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(getSScheduleApiUrl.sHtLuPlanningProcesses(), null, {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const regenerateSPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getSScheduleApiUrl.sPlanningProcessesRegenerateById(processId),
  );
  return res.data.data;
};

const cancelGenerationSPlanningProcess = async (processId: string, generationJobId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getSScheduleApiUrl.sPlanningProcessesCancelGenerationById(processId),
    null,
    { params: { generationJobId } },
  );
  return res.data.data;
};

const cancelSPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getSScheduleApiUrl.sPlanningProcessesCancelById(processId),
  );
  return res.data.data;
};

const publishSPlanningProcess = async (processId: string, scheduleId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getSScheduleApiUrl.sPlanningProcessesPublishById(processId),
    null,
    { params: { scheduleId } },
  );
  return res.data.data;
};

const getSPlanningShifts = async (processId: string, params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<SShiftsResponseBody>>(
    getSScheduleApiUrl.sPlanningProcessesShiftsById(processId),
    { params },
  );
  return res.data.data;
};

const getSValidation = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<SShiftTypeScheduleValidationResult>>(
    getSScheduleApiUrl.sScheduleValidation(scheduleId),
  );
  return res.data.data;
};

const sScheduleRefreshHr = async (scheduleId: string) => {
  const res = await http.post<ApiResponseBody<null>>(getSScheduleApiUrl.sScheduleRefreshHr(scheduleId));
  return res.data.data;
};

const getSScheduleHrSystemConflicts = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<SResourceSShiftTypeHrSystemConflict[]>>(
    getSScheduleApiUrl.sScheduleHrSystemConflicts(scheduleId),
  );
  return res.data.data;
};

const resolveSScheduleHrSystemConflict = async (body: SResolveHrSystemConflict) => {
  const res = await http.post<ApiResponseBody<SShift>>(getSScheduleApiUrl.sScheduleHrSystemConflictsResolve(), body);
  return res.data.data;
};

/** SETTINGS */
const getSLiScheduleRules = async () => {
  const res = await http.get<ApiResponseBody<SResourceRulesSettings>>(getSScheduleApiUrl.sLiSettingsRules());
  return res.data.data;
};
const getSHtLuScheduleRules = async () => {
  const res = await http.get<ApiResponseBody<SResourceRulesSettings>>(getSScheduleApiUrl.sHtLuSettingsRules());
  return res.data.data;
};
const setSLiScheduleRules = async (body: SResourceRulesSettings) => {
  const res = await http.post<ApiResponseBody<SResourceRulesSettings>>(getSScheduleApiUrl.sLiSettingsRules(), body);
  return res.data.data;
};
const setSHtLuScheduleRules = async (body: SResourceRulesSettings) => {
  const res = await http.post<ApiResponseBody<SResourceRulesSettings>>(getSScheduleApiUrl.sHtLuSettingsRules(), body);
  return res.data.data;
};

const getSLiScheduleInputs = async () => {
  const res = await http.get<ApiResponseBody<SRequirementsInputsSettings>>(getSScheduleApiUrl.sLiSettingsInputs());
  return res.data.data;
};
const getSHtLuScheduleInputs = async () => {
  const res = await http.get<ApiResponseBody<SRequirementsInputsSettings>>(getSScheduleApiUrl.sHtLuSettingsInputs());
  return res.data.data;
};

const setSLiScheduleInputs = async (body: SRequirementsInputsSettings) => {
  const res = await http.post<ApiResponseBody<SRequirementsInputsSettings>>(
    getSScheduleApiUrl.sLiSettingsInputs(),
    body,
  );
  return res.data.data;
};
const setSHtLuScheduleInputs = async (body: SRequirementsInputsSettings) => {
  const res = await http.post<ApiResponseBody<SRequirementsInputsSettings>>(
    getSScheduleApiUrl.sHtLuSettingsInputs(),
    body,
  );
  return res.data.data;
};

const sScheduleApiFetcher = {
  getSLiSchedule,
  getSHtLuSchedule,
  getSLiShifts,
  getSHtLuShifts,
  getSLiRegions,
  getSHtLuRegions,
  setSLiShiftType,
  setSHtLuShiftType,
  resetSLiShiftType,
  resetSHtLuShiftType,
  setSFixationType,
  getSLiPlanningProcess,
  getSHtLuPlanningProcess,
  getSPlanningProcessById,
  createSLiPlanningProcess,
  createSHtLuPlanningProcess,
  cancelSPlanningProcess,
  publishSPlanningProcess,
  regenerateSPlanningProcess,
  cancelGenerationSPlanningProcess,
  getSPlanningShifts,
  getSValidation,
  getSLiScheduleRules,
  getSHtLuScheduleRules,
  setSLiScheduleRules,
  setSHtLuScheduleRules,
  getSLiScheduleInputs,
  getSHtLuScheduleInputs,
  setSLiScheduleInputs,
  setSHtLuScheduleInputs,
  sScheduleRefreshHr,
  getSScheduleHrSystemConflicts,
  resolveSScheduleHrSystemConflict,
};

const getCCShifts = async (params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<CCShiftsResponseBody>>(getCCScheduleApiUrl.ccShifts(), { params });
  return res.data.data;
};

const setCCShiftType = async (body: SetCCShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<CCShift>>(getCCScheduleApiUrl.ccSetShiftType(), body);
  return res.data.data;
};

const resetCCShiftType = async (body: ResetShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getCCScheduleApiUrl.ccResetShiftType(), body);
  return res.data.data;
};

const setCCFixationType = async (body: SetFixationTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getCCScheduleApiUrl.ccSetFixationType(), body);
  return res.data.data;
};

const setMyCCShiftType = async (body: SetMyCCShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<CCShift>>(getCCScheduleApiUrl.ccSetMyShiftType(), body);
  return res.data.data;
};

const resetMyCCShiftType = async (body: ResetMyShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getCCScheduleApiUrl.ccResetMyShiftType(), body);
  return res.data.data;
};

const getCCSchedule = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<MonthSchedule>>(getCCScheduleApiUrl.ccSchedule(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getCCPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getCCScheduleApiUrl.ccPlanningProcesses(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getCCPlanningProcessById = async (processId: string) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getCCScheduleApiUrl.ccPlanningProcessesById(processId));
  return res.data.data;
};

const createCCPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(getCCScheduleApiUrl.ccPlanningProcesses(), null, {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const regenerateCCPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCCScheduleApiUrl.ccPlanningProcessesRegenerateById(processId),
  );
  return res.data.data;
};

const cancelGenerationCCPlanningProcess = async (processId: string, generationJobId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCCScheduleApiUrl.ccPlanningProcessesCancelGenerationById(processId),
    null,
    { params: { generationJobId } },
  );
  return res.data.data;
};

const cancelCCPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCCScheduleApiUrl.ccPlanningProcessesCancelById(processId),
  );
  return res.data.data;
};

const publishCCPlanningProcess = async (processId: string, scheduleId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getCCScheduleApiUrl.ccPlanningProcessesPublishById(processId),
    null,
    { params: { scheduleId } },
  );
  return res.data.data;
};

const getCCPlanningShifts = async (processId: string, params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<CCShiftsResponseBody>>(
    getCCScheduleApiUrl.ccPlanningProcessesShiftsById(processId),
    { params },
  );
  return res.data.data;
};

const getCCValidation = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<CCShiftTypeScheduleValidationResult>>(
    getCCScheduleApiUrl.ccScheduleValidation(scheduleId),
  );
  return res.data.data;
};

const ccScheduleRefreshHr = async (scheduleId: string) => {
  const res = await http.post<ApiResponseBody<null>>(getCCScheduleApiUrl.ccScheduleRefreshHr(scheduleId));
  return res.data.data;
};

const getCCScheduleHrSystemConflicts = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<CCResourceCCShiftTypeHrSystemConflict[]>>(
    getCCScheduleApiUrl.ccScheduleHrSystemConflicts(scheduleId),
  );
  return res.data.data;
};

const resolveCCScheduleHrSystemConflict = async (body: CCResolveHrSystemConflict) => {
  const res = await http.post<ApiResponseBody<CCShift>>(getCCScheduleApiUrl.ccScheduleHrSystemConflictsResolve(), body);
  return res.data.data;
};

/** SETTINGS */
const getCCScheduleRules = async () => {
  const res = await http.get<ApiResponseBody<CCResourceRulesSettings>>(getCCScheduleApiUrl.ccSettingsRules());
  return res.data.data;
};

const setCCScheduleRules = async (body: CCResourceRulesSettings) => {
  const res = await http.post<ApiResponseBody<CCResourceRulesSettings>>(getCCScheduleApiUrl.ccSettingsRules(), body);
  return res.data.data;
};

const getCCScheduleInputs = async () => {
  const res = await http.get<ApiResponseBody<CCRequirementsInputsSettings>>(getCCScheduleApiUrl.ccSettingsInputs());
  return res.data.data;
};

const setCCScheduleInputs = async (body: CCRequirementsInputsSettings) => {
  const res = await http.post<ApiResponseBody<CCRequirementsInputsSettings>>(
    getCCScheduleApiUrl.ccSettingsInputs(),
    body,
  );
  return res.data.data;
};

const ccScheduleApiFetcher = {
  getCCSchedule,
  getCCShifts,
  setCCShiftType,
  resetCCShiftType,
  setCCFixationType,
  setMyCCShiftType,
  resetMyCCShiftType,
  getCCPlanningProcess,
  getCCPlanningProcessById,
  createCCPlanningProcess,
  cancelCCPlanningProcess,
  publishCCPlanningProcess,
  regenerateCCPlanningProcess,
  cancelGenerationCCPlanningProcess,
  getCCPlanningShifts,
  getCCValidation,
  getCCScheduleRules,
  setCCScheduleRules,
  getCCScheduleInputs,
  setCCScheduleInputs,
  ccScheduleRefreshHr,
  getCCScheduleHrSystemConflicts,
  resolveCCScheduleHrSystemConflict,
};

/** OMS SCHEDULE */
const getOMSShifts = async (params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<OMSShiftsResponseBody>>(getOMSScheduleApiUrl.omsShifts(), { params });
  return res.data.data;
};

const setOMSShiftType = async (body: SetOMSShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<OMSShift>>(getOMSScheduleApiUrl.omsSetShiftType(), body);
  return res.data.data;
};

const resetOMSShiftType = async (body: ResetShiftTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getOMSScheduleApiUrl.omsResetShiftType(), body);
  return res.data.data;
};

const setOMSFixationType = async (body: SetFixationTypeRequestBody) => {
  const res = await http.post<ApiResponseBody<null>>(getOMSScheduleApiUrl.omsSetFixationType(), body);
  return res.data.data;
};

const getOMSSchedule = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<MonthSchedule>>(getOMSScheduleApiUrl.omsSchedule(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getOMSPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(getOMSScheduleApiUrl.omsPlanningProcesses(), {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const getOMSPlanningProcessById = async (processId: string) => {
  const res = await http.get<ApiResponseBody<PlanningProcess>>(
    getOMSScheduleApiUrl.omsPlanningProcessesById(processId),
  );
  return res.data.data;
};

const createOMSPlanningProcess = async (startDayOfMonth: Date) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(getOMSScheduleApiUrl.omsPlanningProcesses(), null, {
    params: { startDayOfMonth },
  });
  return res.data.data;
};

const regenerateOMSPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getOMSScheduleApiUrl.omsPlanningProcessesRegenerateById(processId),
  );
  return res.data.data;
};

const cancelGenerationOMSPlanningProcess = async (processId: string, generationJobId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getOMSScheduleApiUrl.omsPlanningProcessesCancelGenerationById(processId),
    null,
    { params: { generationJobId } },
  );
  return res.data.data;
};

const cancelOMSPlanningProcess = async (processId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getOMSScheduleApiUrl.omsPlanningProcessesCancelById(processId),
  );
  return res.data.data;
};

const publishOMSPlanningProcess = async (processId: string, scheduleId: string) => {
  const res = await http.post<ApiResponseBody<PlanningProcess>>(
    getOMSScheduleApiUrl.omsPlanningProcessesPublishById(processId),
    null,
    { params: { scheduleId } },
  );
  return res.data.data;
};

const getOMSPlanningShifts = async (processId: string, params: DateRangeParams) => {
  const res = await http.get<ApiResponseBody<OMSShiftsResponseBody>>(
    getOMSScheduleApiUrl.omsPlanningProcessesShiftsById(processId),
    { params },
  );
  return res.data.data;
};

const getOMSValidation = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<OMSShiftTypeScheduleValidationResult>>(
    getOMSScheduleApiUrl.omsScheduleValidation(scheduleId),
  );
  return res.data.data;
};

const omsScheduleRefreshHr = async (scheduleId: string) => {
  const res = await http.post<ApiResponseBody<null>>(getOMSScheduleApiUrl.omsScheduleRefreshHr(scheduleId));
  return res.data.data;
};

const getOMSScheduleHrSystemConflicts = async (scheduleId: string) => {
  const res = await http.get<ApiResponseBody<OMSResourceOMSShiftTypeHrSystemConflict[]>>(
    getOMSScheduleApiUrl.omsScheduleHrSystemConflicts(scheduleId),
  );
  return res.data.data;
};

const resolveOMSScheduleHrSystemConflict = async (body: OMSResolveHrSystemConflict) => {
  const res = await http.post<ApiResponseBody<OMSShift>>(
    getOMSScheduleApiUrl.omsScheduleHrSystemConflictsResolve(),
    body,
  );
  return res.data.data;
};

/** OMS SETTINGS */
const getOMSScheduleRules = async () => {
  const res = await http.get<ApiResponseBody<OMSResourceRulesSettings>>(getOMSScheduleApiUrl.omsSettingsRules());
  return res.data.data;
};

const setOMSScheduleRules = async (body: OMSResourceRulesSettings) => {
  const res = await http.post<ApiResponseBody<OMSResourceRulesSettings>>(getOMSScheduleApiUrl.omsSettingsRules(), body);
  return res.data.data;
};

const getOMSScheduleInputs = async () => {
  const res = await http.get<ApiResponseBody<OMSRequirementsInputsSettings>>(getOMSScheduleApiUrl.omsSettingsInputs());
  return res.data.data;
};

const setOMSScheduleInputs = async (body: OMSRequirementsInputsSettings) => {
  const res = await http.post<ApiResponseBody<OMSRequirementsInputsSettings>>(
    getOMSScheduleApiUrl.omsSettingsInputs(),
    body,
  );
  return res.data.data;
};

const omsScheduleApiFetcher = {
  getOMSSchedule,
  getOMSShifts,
  setOMSShiftType,
  resetOMSShiftType,
  setOMSFixationType,
  getOMSPlanningProcess,
  getOMSPlanningProcessById,
  createOMSPlanningProcess,
  cancelOMSPlanningProcess,
  publishOMSPlanningProcess,
  regenerateOMSPlanningProcess,
  cancelGenerationOMSPlanningProcess,
  getOMSPlanningShifts,
  getOMSValidation,
  omsScheduleRefreshHr,
  getOMSScheduleHrSystemConflicts,
  resolveOMSScheduleHrSystemConflict,
  getOMSScheduleRules,
  setOMSScheduleRules,
  getOMSScheduleInputs,
  setOMSScheduleInputs,
};

export const scheduleApiFetcher = {
  cs: csScheduleApiFetcher,
  s: sScheduleApiFetcher,
  cc: ccScheduleApiFetcher,
  oms: omsScheduleApiFetcher,
};
